import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { apiUrl } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { CallCenterAnnouncementSettings } from '@app/shared/acd-cc/components/call-center-announcements-settings/call-center-announcement-settings';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { CallCenterQueueAnnouncement } from '@business/call-center/call-center-queue-announcement/models/call-center-queue-announcement';
import { iif, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CallCenterQueueAnnouncementRepository {
  constructor(
    private authenticationService: AuthenticationService,
    private http: HttpClient,
    private utils: Utils
  ) {}

  add(
    callCenterId: number,
    queueId: number,
    callCenterQueueAnnouncement: Partial<CallCenterQueueAnnouncement>,
    file: File
  ): Observable<void> {
    const formData = new FormData();

    formData.append('description', callCenterQueueAnnouncement.description);
    formData.append('file', file, file.name);

    return this.utils.apiUrl(`/callCenter/${callCenterId}/queue/${queueId}/announcement`).pipe(
      mergeMap((url) =>
        this.http.post<void>(url, formData, {
          params: {
            type: callCenterQueueAnnouncement.type,
          },
        })
      ),
      wrapError('call-center-queue-announcement-repository:add')
    );
  }

  delete(callCenterId: number, queueId: number, id: number): Observable<void> {
    return this.utils.apiUrl(`/callCenter/${callCenterId}/queue/${queueId}/announcement/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('call-center-queue-announcement-repository:delete')
    );
  }

  list(callCenterId: number, queueId: number): Observable<CallCenterQueueAnnouncement[]> {
    const url = `/callCenter/${callCenterId}/queue/${queueId}/announcement?domainAnnouncementsToo=true`;

    return iif(
      () => this.authenticationService.userIsEndUser(),
      of(apiUrl(`/user${url}`)),
      this.utils.apiUrl(`${url}`)
    ).pipe(
      mergeMap((url) => this.http.get<CallCenterQueueAnnouncement[]>(url)),
      wrapError('call-center-queue-announcement-repository:list')
    );
  }

  update(
    callCenterId: number,
    queueId: number,
    callCenterQueueAnnouncement: CallCenterQueueAnnouncement,
    file?: File
  ): Observable<void> {
    const formData = new FormData();

    formData.append('announcement', JSON.stringify(callCenterQueueAnnouncement));

    if (file != null) {
      formData.append('file', file, file.name);
    }

    return this.utils
      .apiUrl(`/callCenter/${callCenterId}/queue/${queueId}/announcement/${callCenterQueueAnnouncement.id}`)
      .pipe(
        mergeMap((url) => this.http.put<void>(url, formData)),
        wrapError('call-center-queue-announcement-repository:update')
      );
  }

  public updateSettings(callCenterId: number, queueId: number, settings: CallCenterAnnouncementSettings) {
    return this.utils.apiUrl(`/callCenter/${callCenterId}/queue/${queueId}/announcements/settings`).pipe(
      mergeMap((url) => this.http.put<void>(url, settings)),
      wrapError('call-center-queue-announcement-repository:update-call-center-queue-announcement-settings')
    );
  }
}
